package dev.wyns.ventrova.ui.cupertino

import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.gestures.detectVerticalDragGestures
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.input.pointer.pointerInput
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import dev.wyns.ventrova.app.PlaceItem
import dev.wyns.ventrova.app.rememberAppEnvironment
import dev.wyns.ventrova.data.model.fryOilStatus
import dev.wyns.ventrova.resources.Res
import dev.wyns.ventrova.resources.filter_clear_filters
import dev.wyns.ventrova.resources.ic_close
import dev.wyns.ventrova.resources.ic_expand_less
import dev.wyns.ventrova.resources.ic_expand_more
import dev.wyns.ventrova.resources.loading
import dev.wyns.ventrova.resources.map_attribution
import dev.wyns.ventrova.resources.nearby_by_distance
import dev.wyns.ventrova.resources.nearby_count
import dev.wyns.ventrova.resources.nearby_nothing_found
import dev.wyns.ventrova.ui.components.LevelBadge
import dev.wyns.ventrova.ui.components.FryOilLine
import dev.wyns.ventrova.ui.components.LevelTypeDistanceLine
import dev.wyns.ventrova.ui.components.OpenStatusLine
import dev.wyns.ventrova.ui.components.PlaceListSkeleton
import dev.wyns.ventrova.ui.components.PlaceThumbnail
import dev.wyns.ventrova.ui.components.SkeletonLine
import dev.wyns.ventrova.ui.components.VIcon
import dev.wyns.ventrova.ui.components.bodyStyle
import dev.wyns.ventrova.ui.components.filterSummary
import dev.wyns.ventrova.ui.map.PlacesMap
import dev.wyns.ventrova.ui.theme.VentrovaColors
import dev.wyns.ventrova.ui.theme.displayStyle
import dev.wyns.ventrova.ui.theme.label
import dev.wyns.ventrova.ui.theme.walkLabel
import kotlin.math.abs
import org.jetbrains.compose.resources.pluralStringResource
import org.jetbrains.compose.resources.stringResource

/** The full-screen map behind the native chrome of the map tab. */
@Composable
internal fun CupertinoMapContent(model: CupertinoModel) {
    CupertinoTheme(model) {
        val env = rememberAppEnvironment(model.dependencies, model.viewModel)
        DisposableEffect(env) {
            model.environment = env
            onDispose { if (model.environment === env) model.environment = null }
        }
        val state by model.viewModel.state.collectAsState()
        Box(Modifier.fillMaxSize().background(VentrovaColors.MapBackground)) {
            PlacesMap(
                controller = env.map,
                places = state.nearby,
                selectedId = state.selected?.id,
                decorations = CupertinoDecorations,
                onMarkerClick = {
                    model.viewModel.selectPlace(it.id)
                    model.detent.value = PanelDetent.Peek
                    env.focus(it, fractionY = 0.36f)
                },
                onMapClick = { model.viewModel.selectPlace(null) },
                onViewportChanged = model.viewModel::onViewportChanged,
                userLocation = state.userLocation,
                radiusCenter = state.referencePoint,
                radiusKm = state.filters.radiusKm,
                modifier = Modifier.fillMaxSize(),
            )
            Text(
                stringResource(Res.string.map_attribution),
                style = bodyStyle(8.5.sp, color = VentrovaColors.Ink.copy(alpha = 0.5f)),
                modifier = Modifier.align(Alignment.BottomStart).padding(start = 12.dp, bottom = 6.dp),
            )
        }
    }
}

/** Content of the floating Liquid Glass panel: the nearby list or the selected place. */
@Composable
internal fun CupertinoPanelContent(model: CupertinoModel) {
    CupertinoTheme(model) {
        val state by model.viewModel.state.collectAsState()
        val detent by model.detent.collectAsState()
        val selected = state.selected
        if (selected != null) {
            SelectedPlace(selected, onOpen = { model.viewModel.openDetail(selected.id) }, onClose = { model.viewModel.selectPlace(null) })
            return@CupertinoTheme
        }
        Column(Modifier.fillMaxSize()) {
            var drag by remember { mutableStateOf(0f) }
            Column(
                Modifier
                    .fillMaxWidth()
                    .clickable(onClick = model::cycleDetent)
                    .pointerInput(Unit) {
                        detectVerticalDragGestures(
                            onDragStart = { drag = 0f },
                            onDragEnd = {
                                if (abs(drag) > 24) {
                                    val current = model.detent.value.ordinal
                                    model.detent.value = PanelDetent.entries[(current + if (drag < 0) 1 else -1).coerceIn(0, 2)]
                                }
                            },
                            onVerticalDrag = { _, amount -> drag += amount },
                        )
                    }
                    .padding(start = 18.dp, end = 18.dp, top = 8.dp, bottom = 12.dp),
            ) {
                Box(Modifier.align(Alignment.CenterHorizontally).padding(bottom = 9.dp).width(36.dp).height(5.dp).background(VentrovaColors.Ink.copy(alpha = 0.18f), RoundedCornerShape(3.dp)))
                Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(10.dp)) {
                    Column(Modifier.weight(1f)) {
                        if (state.isLoading) {
                            SkeletonLine(width = 170.dp, height = 17.dp, modifier = Modifier.padding(vertical = 2.dp))
                            SkeletonLine(width = 120.dp, height = 11.dp, modifier = Modifier.padding(top = 5.dp))
                        } else {
                            Text(pluralStringResource(Res.plurals.nearby_count, state.nearby.size, state.nearby.size), style = bodyStyle(17.sp, FontWeight.SemiBold))
                            Text(
                                filterSummary(state.filters) + " · " + stringResource(Res.string.nearby_by_distance),
                                style = bodyStyle(13.sp, color = VentrovaColors.Muted),
                                maxLines = 1,
                                overflow = TextOverflow.Ellipsis,
                            )
                        }
                    }
                    VIcon(if (detent == PanelDetent.Full) Res.drawable.ic_expand_more else Res.drawable.ic_expand_less, tint = VentrovaColors.Muted)
                }
            }
            if (detent != PanelDetent.Peek) {
                HorizontalDivider(color = VentrovaColors.Ink.copy(alpha = 0.08f), thickness = 0.5.dp)
                if (state.isLoading) {
                    PlaceListSkeleton(count = 6, modifier = Modifier.padding(horizontal = 8.dp), loadingLabel = stringResource(Res.string.loading))
                } else if (state.nearby.isEmpty()) {
                    Column(Modifier.fillMaxWidth().padding(40.dp), horizontalAlignment = Alignment.CenterHorizontally) {
                        Text(stringResource(Res.string.nearby_nothing_found), style = bodyStyle(15.sp, color = VentrovaColors.Muted), textAlign = TextAlign.Center)
                        Text(
                            stringResource(Res.string.filter_clear_filters),
                            style = bodyStyle(15.sp, FontWeight.SemiBold, VentrovaColors.Accent),
                            modifier = Modifier.padding(top = 10.dp).clickable(onClick = model.viewModel::clearFilters),
                        )
                    }
                }
                LazyColumn(contentPadding = PaddingValues(start = 8.dp, end = 8.dp, bottom = 12.dp)) {
                    items(state.nearby, key = { it.id }) { item ->
                        CupertinoPlaceRow(item, onClick = { model.viewModel.openDetail(item.id) })
                    }
                }
            }
        }
    }
}

@Composable
internal fun CupertinoPlaceRow(item: PlaceItem, onClick: () -> Unit, thumbSize: Int = 58) {
    Row(
        Modifier.fillMaxWidth().clip(RoundedCornerShape(18.dp)).clickable(onClick = onClick).padding(10.dp),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(12.dp),
    ) {
        PlaceThumbnail(item.place, Modifier.size(thumbSize.dp), RoundedCornerShape(15.dp))
        Column(Modifier.weight(1f)) {
            Text(item.place.name, style = bodyStyle(16.sp, FontWeight.SemiBold), maxLines = 1, overflow = TextOverflow.Ellipsis)
            LevelTypeDistanceLine(item, Modifier.padding(top = 2.dp))
            FryOilLine(item.place, Modifier.padding(top = 2.dp))
            OpenStatusLine(item, Modifier.padding(top = 2.dp))
        }
    }
}

@Composable
private fun SelectedPlace(item: PlaceItem, onOpen: () -> Unit, onClose: () -> Unit) {
    Box(Modifier.fillMaxSize().clickable(onClick = onOpen).padding(14.dp)) {
        Row(horizontalArrangement = Arrangement.spacedBy(14.dp), verticalAlignment = Alignment.CenterVertically) {
            PlaceThumbnail(item.place, Modifier.size(118.dp), RoundedCornerShape(20.dp), iconSize = 32.dp)
            Column(Modifier.weight(1f)) {
                // The panel has a fixed height, so the fry oil line takes the name's second line.
                val nameLines = if (item.place.fryOilStatus != null) 1 else 2
                Text(item.place.name, style = displayStyle(21), modifier = Modifier.padding(end = 26.dp), maxLines = nameLines, overflow = TextOverflow.Ellipsis)
                LevelBadge(item.place.veganLevel, Modifier.padding(top = 7.dp))
                FryOilLine(item.place, Modifier.padding(top = 2.dp))
                OpenStatusLine(item, Modifier.padding(top = 5.dp))
                Text(
                    listOfNotNull(stringResource(item.place.category.label), item.distanceMeters?.let { walkLabel(it) }).joinToString(" · "),
                    style = bodyStyle(13.sp, color = VentrovaColors.Secondary),
                    modifier = Modifier.padding(top = 2.dp),
                )
            }
        }
        Box(
            Modifier.align(Alignment.TopEnd).size(30.dp).clip(CircleShape).background(VentrovaColors.Ink.copy(alpha = 0.07f)).clickable(onClick = onClose),
            contentAlignment = Alignment.Center,
        ) {
            VIcon(Res.drawable.ic_close, size = 18.dp, tint = VentrovaColors.Secondary)
        }
    }
}
